import { useNavigate } from "react-router-dom";
import { ArrowRight, MessageSquare, Phone, Store } from "lucide-react";
import { paths } from "@/app/routes/paths";
import type { Trip, TripStatus } from "@/entities/trip";
import { useAgentIdentity } from "@/features/agent/hooks/useAgentIdentity";
import { tripIntervenants } from "@/features/trips";
import { TrackingMap } from "@/features/tracking";
import { useTripLiveMap } from "@/features/trips/hooks/useTripLiveMap";
import { colors } from "@/shared/design-system";
import { haversineKm } from "@/shared/geo/distance";
import { etaMinutesFromKm } from "@/shared/geo/eta";
import { AppText, Card, StatusBadge } from "@/shared/ui";
import { TripProgressSteps } from "./TripProgressSteps";

/**
 * Phrase d'état à hauteur de client, sous le nom du magasin.
 *
 * `tripStatusLabel` du module de format n'est pas réutilisé ici : il parle la
 * langue du système (« Fonds bloqués », « Validation requise »), ce qui est
 * juste dans une liste de courses ou côté agent, mais sec sous le nom d'un
 * magasin. Ici on décrit ce qui se PASSE pour le client.
 */
const STATUS_SENTENCE: Record<TripStatus, string> = {
  PENDING_WALLET_CHECK: "Vérification de votre solde",
  WALLET_FUNDS_BLOCKED: "Recherche d'une Main disponible",
  AGENT_OFFER_PENDING: "Une Main est sollicitée",
  AGENT_ASSIGNED: "Préparation de votre commande",
  AGENT_EN_ROUTE: "Votre Main est en route",
  IN_PROGRESS: "Préparation de votre commande",
  AWAITING_CLIENT_APPROVAL: "Votre validation est requise",
  APPROVAL_EXPIRED: "Délai de validation dépassé",
  DELIVERING: "Livraison en cours",
  DELIVERED: "Commande livrée",
  PAYMENT_SETTLED: "Course réglée",
  CANCELLED: "Course annulée",
  DISPUTED: "Course en litige",
  REFUNDED: "Course remboursée",
};

/** Bouton rond de contact (appel / message). */
function ContactButton({
  label,
  href,
  Icon,
}: {
  label: string;
  href?: string;
  Icon: typeof Phone;
}) {
  const className =
    "flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-or-50 transition-colors hover:bg-or-100 disabled:opacity-40 disabled:hover:bg-or-50";

  // Sans numéro au contrat, le bouton reste VISIBLE mais désactivé : la place
  // qu'il occupe dans la carte est la même, et le client comprend que le canal
  // existe sans cliquer dans le vide.
  if (!href) {
    return (
      <button type="button" disabled aria-label={`${label} (indisponible)`} className={className}>
        <Icon size={16} color={colors.navy800} aria-hidden />
      </button>
    );
  }

  return (
    <a href={href} aria-label={label} className={className}>
      <Icon size={16} color={colors.navy800} aria-hidden />
    </a>
  );
}

export function ActiveTripPanel({ trip }: { trip?: Trip }) {
  const navigate = useNavigate();
  // Hooks appelés AVANT toute sortie anticipée — ils tolèrent l'absence de
  // course (requête désactivée, carte nulle) et l'ordre des hooks reste stable
  // entre le rendu « course en cours » et le rendu vide.
  const agent = useAgentIdentity(trip?.agent_id);
  // Le bloc `intervenants` de la course fait AUTORITÉ sur le nom et le numéro
  // de la Main depuis le 2026-08-26 : c'est précisément le doublon que le
  // contrat vient supprimer — « les trois personnes existaient déjà, mais
  // éparpillées dans trois adresses différentes ; chaque écran les recomposait
  // à sa façon ». `useAgentIdentity` reste en repli, pour l'avatar et la note,
  // que le bloc ne porte pas.
  const mainIntervenant = tripIntervenants(trip)[1];
  const { mapData, userLocation } = useTripLiveMap(trip);

  // Sans course vivante, la section reste en place avec son en-tête et une
  // carte d'attente : la colonne garde sa structure, le client sait où sa
  // prochaine course s'affichera, et rien ne se déplace quand elle démarre.
  if (!trip) {
    return (
      <section aria-labelledby="aside-active-trip">
        <div className="mb-3 flex items-center justify-between gap-2">
          <AppText as="h2" id="aside-active-trip" variant="bodyBold" color={colors.navy800}>
            Course en cours
          </AppText>
          <StatusBadge label="Aucune" tone="neutral" size="sm" />
        </div>

        <Card className="flex flex-col items-center px-4 py-7 text-center">
          <span className="mb-2" style={{ fontSize: 26, lineHeight: 1 }} aria-hidden>
            🛵
          </span>
          <AppText as="p" variant="smallStrong" color={colors.navy800}>
            Aucune course en cours
          </AppText>
          <AppText as="p" variant="tiny" color={colors.gray500} className="mt-1">
            Votre livraison s'affichera ici, en direct.
          </AppText>

          <button
            type="button"
            onClick={() => navigate(paths.newTrip({ mode: "express" }))}
            className="mt-3.5 rounded-full bg-or-50 px-4 py-2 transition-colors hover:bg-or-100"
          >
            <AppText variant="tinyBold" color={colors.navy800}>
              Commander maintenant
            </AppText>
          </button>
        </Card>
      </section>
    );
  }

  // Arrêt courant = le commerce où la Main se trouve. Le contrat ne nomme pas
  // les enseignes (aucun référentiel) : `location.address` est le libellé le
  // plus proche dont on dispose.
  const stops = trip.stops ?? [];
  const currentStop = stops[trip.current_stop_index ?? 0];
  const stopLabel = currentStop?.location?.address ?? "Course en préparation";

  // ETA dérivée de la position GPS live de la Main : distance à vol d'oiseau
  // jusqu'au point de livraison, à la vitesse ville du module de matching.
  // Tant qu'aucune position n'est reçue, la ligne disparaît — un délai inventé
  // serait pire que pas de délai.
  const etaMinutes = mapData
    ? etaMinutesFromKm(haversineKm(mapData.agentLocation, mapData.dropoffLocation))
    : null;

  // ⚠️ `intervenants.main.telephone` sera souvent `null`, et durablement : le
  // numéro d'une Main n'a aucune source dans la plateforme aujourd'hui. D'où le
  // repli sur la fiche agent, et les boutons qui restent DÉSACTIVÉS plutôt
  // qu'absents quand aucun des deux ne donne de numéro.
  const phone = mainIntervenant.telephone ?? agent.data?.phone;
  const mainName = mainIntervenant.nom ?? agent.data?.name;

  return (
    <section aria-labelledby="aside-active-trip">
      <div className="mb-3 flex items-center justify-between gap-2">
        <AppText as="h2" id="aside-active-trip" variant="bodyBold" color={colors.navy800}>
          Course en cours
        </AppText>
        {/* Libellé fixe : le panneau n'existe que pour une course vivante, et le
            statut détaillé est déjà dit en toutes lettres sous le magasin. */}
        <StatusBadge label="En cours" tone="success" size="sm" />
      </div>

      <Card>
        <div className="flex items-center gap-3 p-4">
          {agent.data?.avatarUrl ? (
            <img
              src={agent.data.avatarUrl}
              alt=""
              className="h-11 w-11 shrink-0 rounded-full object-cover"
            />
          ) : (
            <span
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-or-50"
              style={{ fontSize: 20 }}
              aria-hidden
            >
              {agent.data?.avatar ?? "🧑"}
            </span>
          )}

          <div className="min-w-0 flex-1">
            <AppText as="p" variant="tiny" color={colors.gray500}>
              Votre livreur
            </AppText>
            <div className="flex items-baseline gap-1.5">
              <AppText as="p" variant="labelBold" color={colors.navy800} className="truncate">
                {mainName ?? "…"}
              </AppText>
              {/* Une note absente n'est pas une note de 0 : sans `qualityScore`,
                  l'étoile disparaît plutôt que d'annoncer une mauvaise Main. */}
              {agent.data?.rating != null && (
                <AppText as="span" variant="tinyBold" color={colors.navy800}>
                  <span style={{ color: colors.or500 }}>★</span> {agent.data.rating.toFixed(1)}
                </AppText>
              )}
            </div>
          </div>

          <div className="flex shrink-0 gap-2">
            <ContactButton
              label={`Appeler ${mainName ?? "la Main"}`}
              href={phone ? `tel:${phone}` : undefined}
              Icon={Phone}
            />
            <ContactButton
              label={`Écrire à ${mainName ?? "la Main"}`}
              href={phone ? `sms:${phone}` : undefined}
              Icon={MessageSquare}
            />
          </div>
        </div>

        <div className="border-t border-gray-100 px-4 pt-3.5">
          <div className="flex items-start gap-2.5">
            <Store size={16} color={colors.navy800} aria-hidden className="mt-0.5 shrink-0" />
            <div className="min-w-0">
              <AppText as="p" variant="smallStrong" color={colors.navy800} className="truncate">
                {stopLabel}
              </AppText>
              <AppText as="p" variant="tiny" color={colors.or700}>
                {trip.status ? STATUS_SENTENCE[trip.status] : "Course en cours"}
              </AppText>
            </div>
          </div>

          {etaMinutes != null && (
            <div className="mt-3.5">
              <AppText as="p" variant="tiny" color={colors.gray500}>
                Livraison estimée
              </AppText>
              <AppText as="p" variant="h3" color={colors.navy800}>
                {etaMinutes} min
              </AppText>
            </div>
          )}

          <div className="py-4">
            <TripProgressSteps status={trip.status} />
          </div>
        </div>

        {/* La carte n'apparaît qu'une fois une position GPS reçue par socket ;
            un cadre vide en attendant se lirait comme une carte cassée. */}
        {mapData && (
          <div className="px-4">
            <div className="h-32 w-full overflow-hidden rounded-card">
              <TrackingMap
                agentLocation={mapData.agentLocation}
                pickupLocation={mapData.pickupLocation}
                dropoffLocation={mapData.dropoffLocation}
                routePoints={mapData.routePoints}
                userLocation={userLocation}
              />
            </div>
          </div>
        )}

        <div className="p-4">
          <button
            type="button"
            onClick={() => trip.id && navigate(paths.tripTracking(trip.id))}
            className="flex w-full items-center justify-between gap-2 rounded-btn border border-gray-200 px-4 py-3 text-left transition-colors hover:bg-gray-50"
          >
            <AppText variant="smallStrong" color={colors.navy800}>
              Suivre la course
            </AppText>
            <ArrowRight size={16} color={colors.navy800} aria-hidden />
          </button>
        </div>
      </Card>
    </section>
  );
}
